(() => {
  const root = document.documentElement;
  const storageKey = 'gm3d-lighting-mode';
  const attributeName = 'data-gm3d-lighting';
  const validModes = new Set(['off', 'on']);

  const normalizeMode = (value) => (validModes.has(value) ? value : null);
  const currentMode = () => normalizeMode(root.getAttribute(attributeName)) || 'off';

  const persistMode = (mode) => {
    try {
      window.localStorage.setItem(storageKey, mode);
    } catch {
      return;
    }
  };

  const statusText = (status, mode) => {
    const configuredText =
      status.getAttribute(`data-gm3d-lighting-status-${mode}`) ||
      status.getAttribute(`data-gm3d-lighting-${mode}-text`);
    if (configuredText) return configuredText;

    const container = status.closest('[data-gm3d-lighting-surface], section') || document;
    const activeControl =
      container.querySelector(`[data-gm3d-lighting-toggle][data-gm3d-lighting-value="${mode}"]`) ||
      document.querySelector(`[data-gm3d-lighting-toggle][data-gm3d-lighting-value="${mode}"]`);

    return activeControl?.textContent.trim() || '';
  };

  const syncInterface = (scope = document, mode = currentMode()) => {
    scope.querySelectorAll('[data-gm3d-lighting-toggle]').forEach((control) => {
      const controlMode = normalizeMode(control.getAttribute('data-gm3d-lighting-value'));
      const isActive = controlMode ? controlMode === mode : mode === 'on';
      control.setAttribute('aria-pressed', String(isActive));
      control.classList.toggle('is-active', isActive);
    });

    scope.querySelectorAll('[data-gm3d-lighting-pair]').forEach((pair) => {
      pair.setAttribute('data-gm3d-lighting-current', mode);
      const pairLabel = pair.getAttribute(`data-gm3d-lighting-alt-${mode}`);
      if (pairLabel) pair.setAttribute('aria-label', pairLabel);
      pair.querySelectorAll('[data-gm3d-lighting-image]').forEach((image) => {
        const imageMode = normalizeMode(image.getAttribute('data-gm3d-lighting-image'));
        image.setAttribute('aria-hidden', String(imageMode !== mode));
      });
    });

    scope.querySelectorAll('[data-gm3d-lighting-status]').forEach((status) => {
      const text = statusText(status, mode);
      if (text) status.textContent = text;
    });
  };

  const applyMode = (value, options = {}) => {
    const mode = normalizeMode(value) || 'off';
    const previousMode = currentMode();
    root.setAttribute(attributeName, mode);
    syncInterface(document, mode);

    if (options.persist) persistMode(mode);

    if (previousMode !== mode || options.forceEvent) {
      document.dispatchEvent(
        new CustomEvent('gm3d:lighting-change', {
          detail: { mode },
        }),
      );
    }
  };

  const transitionToMode = (mode, options = {}) => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (document.startViewTransition && !reduceMotion) {
      document.startViewTransition(() => applyMode(mode, options));
      return;
    }
    applyMode(mode, options);
  };

  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-gm3d-lighting-toggle]');
    if (!control) return;

    const requestedMode = normalizeMode(control.getAttribute('data-gm3d-lighting-value'));
    const nextMode = requestedMode || (currentMode() === 'on' ? 'off' : 'on');
    transitionToMode(nextMode, { persist: true });
  });

  document.addEventListener('shopify:section:load', (event) => {
    syncInterface(event.target, currentMode());
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== storageKey) return;
    applyMode(normalizeMode(event.newValue) || 'off');
  });

  window.GM3DLightingMode = {
    get: currentMode,
    set: (mode) => transitionToMode(mode, { persist: true }),
  };

  applyMode(currentMode());
})();
